
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, Loader2, User } from "lucide-react";
import { useQuery as useConvexQuery } from "convex/react";
import { api } from "../../api";
import type { EnhancedUser } from "./enhanced/EnhancedUsersSection";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface LinkUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectUser: (userId: string) => void;
  deviceName: string;
  isLinking?: boolean;
}

export const LinkUserModal = ({ isOpen, onClose, onSelectUser, deviceName, isLinking = false }: LinkUserModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Get current user to access role
  const { user } = useCurrentUser();
  
  // Fetch users data from Convex
  const usersData = useConvexQuery(
    api.users.list.all, 
    { 
      paginationOpts: {
        cursor: null,
        numItems: 100
      }
    }
  );

  // Transform Convex data to match our interface
  const users: EnhancedUser[] = usersData?.page ? usersData.page.map((user: any) => ({
    id: user._id,
    email: user.email || '',
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    status: 'active',
    device_management_type: 'self_managed',
    is_device_owner: false,
    device_count: 0,
    managed_users_count: 0,
    management_roles: [],
    phone: user.phone || '',
    created_at: user._creationTime ? new Date(user._creationTime).toISOString() : '',
    parent: undefined
  })) : [];

  const isLoading = usersData === undefined;

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  const handleUserSelect = (userId: string) => {
    onSelectUser(userId);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Link User to Device
          </DialogTitle>
          <DialogDescription>
            Select a user to link to device "{deviceName}". This will associate the user with this device.
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Users Table */}
        <div className="flex-1 overflow-auto border rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-gray-500">
              {searchTerm ? "No users found matching your search." : "No users available to link."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="font-medium">
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}`
                          : user.first_name || user.last_name || 'Unknown User'
                        }
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {user.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => handleUserSelect(user.id)}
                        size="sm"
                        disabled={isLinking}
                        className="h-8"
                      >
                        {isLinking ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            Linking...
                          </>
                        ) : (
                          'Select'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLinking}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
